import { Injectable } from "@angular/core";
import {
  IAnswerToQuestion,
  INewQuestionnaireResponse,
  INextStepsAfterQuestionnaire,
  IQuestionnaireResponseSerializable,
  QRStatus,
} from "@gloria-public/questionnaire/domain/interfaces/questionnaire-response.interface";
import { IQuestionnaireSerializable } from "@gloria-public/questionnaire/domain/interfaces/questionnaire.interface";
import { ApiService } from "@shared/services/api.service";
import { SessionService } from "@shared/services/session.service";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class QuestionnaireService {
  private get isAuthRequired(): boolean {
    return this.sessionService.isLoggedIn;
  }

  constructor(
    private api: ApiService,
    private sessionService: SessionService
  ) {}

  /**
   * Recover the questions of a questionnaire
   * @param questionnaireId The id of the questionnaire to recover
   * @param language
   * @returns The questions of a questionnaire
   */
  public getQuestionnaire(questionnaireId: string, language: string): Observable<IQuestionnaireSerializable> {
    return this.api.get(
      `questionnaires/${encodeURIComponent(questionnaireId)}`,
      {
        lang: language,
      },
      false
    ) as Observable<IQuestionnaireSerializable>;
  }

  /**
   * Recover the questionnaire responses based on filters
   * @param questionnaireId ID of the questionnaire that the response is linked to.(Must be 'questionnaire/lang')
   * @param questionnaireStatus Status of the questionnaire response
   * @param questionnaireDate Publication date of the questionnaire, in ISO standard
   * @returns The questionnaire response
   */
  public getQuestionnaireResponse(
    questionnaireId: string,
    questionnaireStatus: QRStatus,
    questionnaireDate: string
  ): Observable<IQuestionnaireResponseSerializable[]> {
    return this.api.get(
      this.api.getUpdatedPath("questionnaires/responses"),
      { status: questionnaireStatus, questionnaire: questionnaireId, questionnaireDate },
      this.isAuthRequired,
      this.api.appendGuestHeader()
    ) as Observable<IQuestionnaireResponseSerializable[]>;
  }

  /**
   * Add a new response to a given questionnaire
   * @param newQuestionnaireResponse
   * @returns The newly added questionnaire response
   */
  public addNewQuestionnaireResponse(
    newQuestionnaireResponse: INewQuestionnaireResponse,
    riskTemplate: string
  ): Observable<IQuestionnaireResponseSerializable> {
    return this.api.post(
      this.api.getUpdatedPath("questionnaires/responses"),
      newQuestionnaireResponse,
      this.api.appendGuestHeader(),
      this.isAuthRequired,
      { riskTemplate: riskTemplate }
    ) as Observable<IQuestionnaireResponseSerializable>;
  }

  /**
   * Answer a question in a questionnaire
   * @param answer
   * @returns Succes or error
   */
  public answerToQuestion(answer: IAnswerToQuestion): Observable<INextStepsAfterQuestionnaire> {
    return this.api.patch(
      this.api.getUpdatedPath("questionnaires/responses"),
      answer,
      this.isAuthRequired,
      this.api.appendGuestHeader()
    ) as Observable<INextStepsAfterQuestionnaire>;
  }

  /**
   * Delete a questionnaire response
   * @param id questionnaire response identifier
   * @returns Success or error
   */
  public deleteQuestionnaireResponse(id: string): Observable<unknown> {
    return this.api.delete(
      this.api.getUpdatedPath(`questionnaires/responses/${encodeURIComponent(id)}`),
      undefined,
      this.isAuthRequired,
      this.api.appendGuestHeader()
    );
  }

  /**
   * Recover the next steps triggered by a questionnaire response.
   * @param questionnaireId ID of the questionnaire response
   * @returns The next steps after questionnaire
   */
  public getNextStepsAfterQuestionnaire(questionnaireId: string): Observable<INextStepsAfterQuestionnaire> {
    return this.api.get(
      this.api.getUpdatedPath(`questionnaire-responses/${encodeURIComponent(questionnaireId)}/next-steps`),
      undefined,
      this.isAuthRequired,
      this.api.appendGuestHeader()
    ) as Observable<INextStepsAfterQuestionnaire>;
  }
}
